<template>
  <CModal :show.sync="show" centered :closeOnBackdrop="canClose" add-content-classes="fit-content-modal">
    <PComponentLoader :loading="loading"/>
    <template #header>
      <div>
        <div class="d-flex">
          <CIcon name="cipPlus" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div>
            <h5 class="modal-title">{{stepTitle}}</h5>
            <div class="modal-subtitle">{{stepDescription}}</div>
          </div>
        </div>
      </div>
      <CButtonClose @click="close" v-if="canClose"/>
    </template>

    <template v-if="!loading">
      <template v-if="step === steps.REGION">
        <RegionSelector
            :regions="regions"
            :selected="regionId"
            @input="chooseRegion"
        />
      </template>
      <template v-else-if="step === steps.CONTRACT">
        <ContractSelector
            :contracts="validContracts"
            :selected="contractId"
            @input="chooseContract"
        />
      </template>
      <template v-else-if="step === steps.CATEGORY">
        <CategorySelector
            :currency="region.currency"
            :categories="categories"
            :selected="basePlanId"
            @change="chooseBasePlan"
        />
      </template>
      <template v-else-if="step === steps.BASE_PLAN">
        <BasePlanSwitcher
            :plan="selectedBasePlan"
            :plans="basePlans"
            :currency="region.currency"
            @change="chooseBasePlan"
            class="mb-4"
            v-if="basePlans.length > 0"
        />
      </template>
      <template v-else-if="step === steps.EVENT">
        <PFRow size="12x12">
          <template #title>
            <label class="font-weight-medium mb-2">{{
                $pgettext('event.modal.create_new_event.label', 'Event Name')
              }}</label>
          </template>
          <CInput v-model="v$.form.name.$model"
                  addWrapperClasses="has-counter"
                  :maxlength="v$.form.name.maxLength.$params.max"
                  :placeholder="$pgettext('event.modal.create_new_event.placeholder','Insert...')">
            <template v-slot:description>
              <div class="counter">{{
                  v$.form.name.$model ? v$.form.name.$model.length : 0
                }}/{{ v$.form.name.maxLength.$params.max }}
              </div>
            </template>
          </CInput>
          <div class="text-help-dark text-help-dark--sm" style="margin-top: -8px" v-translate
               translate-context="event.modal.create_new_event.help">You can change it later
          </div>
        </PFRow>
      </template>
      <template v-else-if="step === steps.CONTACT_US">
        Contract Us!
      </template>
    </template>
    <template #footer>
      <CButton color="secondary" @click="close" v-translate v-if="canClose">Close</CButton>
      <PButton
          color="primary"
          class="mr-3"
          :disabled="buttonDisabled"
          :loading="creating"
          @click="moveToNextStep"
      >{{ buttonLabel }}
      </PButton>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import PFRow from "@/domain/core/components/PFRow.vue";
import PButton from "@/components/PButton.vue";
import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";
import RegionSelector from "@/domain/events/components/RegionSelector.vue";
import ContractSelector from "@/domain/events/components/ContractSelector.vue";
import timeZone from "@/domain/core/utils/timeZone";
import {useVuelidate} from '@vuelidate/core'
import {maxLength, minLength, required} from '@vuelidate/validators'
import BasePlanSwitcher from "@/domain/events/components/BasePlanSwitcher.vue";
import CategorySelector from "@/domain/events/components/CategorySelector.vue";
import eventBus from "@/domain/core/constant/eventBus";

const steps = {
  REGION: 'region',
  CONTRACT: 'contract',
  BASE_PLAN: 'base_plan',
  EVENT: 'event',
  CATEGORY: 'category',
  CONTACT_US: 'contact_us'
}
export default {
  name: "CreateNewEventModal",
  components: {
    CategorySelector,
    BasePlanSwitcher,
    ContractSelector,
    RegionSelector,
    PComponentLoader,
    PButton,
    PFRow
  },
  data() {
    return {
      show: false,
      loading: true,
      creating: false,
      isFirstEvent: false,
      canClose: true,
      form: {
        name: null,
        contract: null,
        base_plan: null,
        timezone: null
      },
      selectedRegion: null,
      selectedContract: null,
      selectedBasePlan: null,
    }
  },
  computed: {
    ...mapGetters('events', [
      'regions',
      'region',
      'contracts',
      'basePlans',
      'contract',
      'categories',
      'urlSessionParams'
    ]),
    steps() {
      return steps
    },
    validContracts() {
       if (!this.contracts) return []
      return this.contracts.filter(c => c.qty_available > 0)
    },
    step() {
      if (!this.region) return steps.REGION
      if (!this.form.base_plan && this.basePlans && this.basePlans.length) return steps.BASE_PLAN
      if (!this.form.base_plan && this.categories && this.categories.length) return steps.CATEGORY
      // if (!this.form.contract && this.validContracts.length) return steps.CONTRACT
      if (!this.selectedContract && !this.selectedBasePlan) return steps.CONTACT_US
      return steps.EVENT
    },
    buttonDisabled() {
      if (this.step === steps.REGION) return !this.selectedRegion
      if (this.step === steps.CONTRACT) return !this.selectedContract
      if (this.step === steps.CATEGORY) return !this.selectedBasePlan
      if (this.step === steps.BASE_PLAN) return !this.selectedBasePlan
      if (this.step === steps.EVENT) return !this.isFormReady
      return true
    },
    buttonLabel() {
      if (this.step === steps.EVENT) return this.$pgettext('event.modal.create_new_event.actions', 'Create')
      return this.$pgettext('event.modal.create_new_event.actions', 'Next')
    },
    regionId() {
      return this.selectedRegion ? parseInt(this.selectedRegion.id) : null
    },
    contractId() {
      return this.selectedContract ? parseInt(this.selectedContract.id) : null
    },
    basePlanId() {
      return this.selectedBasePlan ? parseInt(this.selectedBasePlan.id) : null
    },
    isFormReady() {
      if (!this.contractSelected) return false
      return !this.v$.$invalid;
    },
    contractSelected() {
      return this.form.contract !== null || this.form.base_plan !== null
    },
    stepTitle() {
      if (this.isFirstEvent) return this.$pgettext('event.modal.create_new_event.title', 'Create event')
      return this.$pgettext('event.modal.create_new_event.title', 'New event')
    },
    stepDescription() {
      if (this.step === steps.REGION) return this.$pgettext('event.modal.create_new_event.step_description', 'Select region')
      if (this.step === steps.CONTRACT) return this.$pgettext('event.modal.create_new_event.step_description', 'Select contract')
      if (this.step === steps.CATEGORY) return this.$pgettext('event.modal.create_new_event.step_description', 'Distribute photos at your events')
      return this.$pgettext('event.modal.create_new_event.step_description', 'Create new event')
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  validations() {
    return {
      form: {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(this.hasPerm('advanced.bypass_char_limits') ? 255 : 44)
        },
      }
    }
  },
  methods: {
    ...mapActions('events', [
      'fetchCreateConfig',
      'fetchCategories',
      'setRegion',
      'createNewEvent'
    ]),
    async open({canClose, isFirstEvent, contract}) {
      this.show = true
      this.loading = true

      this.canClose = canClose || true
      this.isFirstEvent = isFirstEvent || false

      this.selectedRegion = null
      this.selectedContract = contract
      this.selectedBasePlan = null
      this.creating = false

      await this.fetchCreateConfig(this.selectedContract ? this.selectedContract.id : null)

      // Sync selectedRegion if region is pre-assigned (e.g., via URL param or user's region)
      if (this.region) {
        this.selectedRegion = this.region
      }

      this.form = {
        name: null,
        contract: this.selectedContract ? this.selectedContract.id : null,
        base_plan: this.selectedBasePlan ? this.selectedBasePlan.id : null,
        timezone: timeZone.getBrowserDefault()
      }
      if (this.urlSessionParams) {
        if (!this.regionId && this.urlSessionParams.region && this.regions) {
          await this.selectRegionById(this.urlSessionParams.region)
        }
        if (this.urlSessionParams.plan) {
          // We need to check if the plan is available in the region
          await this.selectPlanById(this.urlSessionParams.plan)
        } else if (this.urlSessionParams.category) {
          // If no plan specified, pre-select first plan of the category
          await this.selectCategoryByName(this.urlSessionParams.category)
        }
      }

      // Auto-select region if there's only one
      if (!this.selectedRegion && this.regions && this.regions.length === 1) {
        await this.selectRegionById(this.regions[0].id)
      }
      this.loading = false
    },
    async selectRegionById(regionId) {
      if (!this.regions) return false
      const region = this.regions.find(r => r.id === parseInt(regionId))
      if (region) {
        this.selectedRegion = region
        await this.onSelectRegion()
        return true
      }
      return false
    },
    async chooseRegion(region) {
      this.selectedRegion = region
    },
    async onSelectRegion() {
      this.loading = true
      await this.setRegion(this.selectedRegion)
      await this.fetchCategories()
      this.loading = false
    },
    async selectPlanById(planId) {
      if (!this.categories) return false
      let found = false
      this.categories.forEach(c => {
        const plan = c.base_plans.find(p => p.id === parseInt(planId))
        if (plan) {
          this.selectedBasePlan = plan.id
          this.onSelectPlan()
          found = true
        }
      })
      return found
    },
    async selectCategoryByName(categoryName) {
      if (!this.categories) return false
      const category = this.categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase())
      if (category && category.base_plans && category.base_plans.length > 0) {
        // Select the first plan of the category
        const firstPlan = category.base_plans[0]
        this.selectedBasePlan = firstPlan.id
        this.onSelectPlan()
        return true
      }
      return false
    },
    async chooseBasePlan(basePlan) {
      this.selectedBasePlan = basePlan
    },
    async onSelectPlan() {
      this.form.base_plan = this.selectedBasePlan
    },
    async chooseContract(contract) {
      this.selectedContract = contract
    },
    async onChangeContract() {
      this.form.contract = this.selectedContract.id
    },
    async create() {
      this.creating = true
      try {
        this.form.timezone = timeZone.getBrowserDefault()
        const item = await this.createNewEvent(this.form)
        await this.emitEvent(eventBus.events.created)
        await this.$router.push({name: 'eventWizard.date_and_name', params: {picaServiceId: item['id']}})
        this.show = false
      } catch (e) {
        this.creating = false
        await this.notifyMessage({text: e.message, messageType: 'danger'})
      }
    },
    moveToNextStep() {
      if (this.step === steps.REGION) {
        this.onSelectRegion()
      } else if (this.step === steps.CONTRACT) {
        this.onChangeContract(this.selectedContract)
      }else if (this.step === steps.CATEGORY) {
        this.onSelectPlan()
      } else if (this.step === steps.BASE_PLAN) {
        this.onSelectPlan()
      } else if (this.step === steps.EVENT) {
        this.create()
      }
    },
    close() {
      this.show = false
      this.emitEvent(eventBus.events.modal_create_event_closed)
    }
  },
}
</script>
